import React, { FC, ReactElement, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Session, updateHeaderConfig } from 'src/front/api/apiInstance';
import { CardSelectionType, ScoringResultType } from 'src/front/api/generated';
import Application from 'src/front/components/Application';
import { StepperRefProps } from 'src/front/components/StepperView';
import { BankruptProvider } from 'src/front/contexts/BankruptContext';

import CardEntryProvider, {
  useCardTypeState,
  useClearDispatch,
  useProductOfferState,
  useScoringState,
} from 'src/front/contexts/CardEntryContext';

import { CARD_ENTRY, RESULT, SubMenuIds } from 'src/front/routes/paths';
import AddressEntry from './address-entry/AddressEntry';
import ApplicationDocumentModal from './ApplicationDocumentUpload';
import Benefits3rdParty from './Benefits3rdParty';
import CardApplicationDetail from './card-information/CardApplicationDetail';
import CardSendingInformation from './card-information/CardSendingInformation';
import ProductOffers from './card-information/ProductOffers';
import CustomerDetailEntry from './CustomerDetailEntry';
import CustomerSearch from './CustomerSearch';
import DocumentEntry from './document-entry/DocumentEntry';
import IndividualInformation from './IndividualInformation';
import PhoneEmailEntry from './phone-email-entry/PhoneEmailEntry';

import ProductInformation from './product-information/ProductInformation';
import ScoringInformation from './scoring/ScoringInformation';
import Summary from './Summary';
import TaxInformation from './tax-information/TaxInformation';

const CardEntry: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const clearOldData = useClearDispatch();
  const cardType = useCardTypeState();
  const isProductOffer = useProductOfferState();
  const isScoringDone = useScoringState();
  const stepperRef = useRef<StepperRefProps>(null);

  const [customerId, setCustomerId] = useState<string>();

  const goToNextStep = async (id?: string): Promise<void> => {
    setCustomerId(id);
    stepperRef.current?.goToNextStep();
  };

  const onRepeat = async (): Promise<void> => {
    const { data, headers } = await Session.clearSessionData({
      menuID: SubMenuIds.CARD_ENTRY,
    });
    if (data.result === 'OK') {
      clearOldData();
      updateHeaderConfig(data.sessionID, headers.authorization);
      history.push(CARD_ENTRY);
    }
  };

  const goToSuccessStep = (message: string, status?: ScoringResultType): void =>
    history.push(RESULT, { message, status, onRepeat });

  const steps = [
    {
      component: () => (
        <ProductInformation disablePage={isScoringDone} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => <CustomerSearch disablePage={isScoringDone} goToNextStep={goToNextStep} />,
    },
    {
      component: () => (
        <IndividualInformation disablePage={isScoringDone} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => (
        <DocumentEntry
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    ...(cardType === CardSelectionType.CREDITCARD
      ? [
          {
            component: () => (
              <ScoringInformation
                customerId={customerId}
                disablePage={isScoringDone}
                goToNextStep={goToNextStep}
              />
            ),
          },
        ]
      : []),
    {
      component: () => (
        <TaxInformation
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    {
      component: () => (
        <CustomerDetailEntry
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    {
      component: () => (
        <Benefits3rdParty
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    {
      component: () => (
        <AddressEntry
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    {
      component: () => (
        <PhoneEmailEntry
          customerId={customerId}
          disablePage={isScoringDone}
          goToNextStep={goToNextStep}
        />
      ),
    },
    ...(isProductOffer
      ? [
          {
            component: () => (
              <ProductOffers
                disablePage={isScoringDone}
                goToFailureStep={goToSuccessStep}
                goToNextStep={goToNextStep}
              />
            ),
          },
        ]
      : []),
    {
      component: () => (
        <CardApplicationDetail customerId={customerId} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => (
        <CardSendingInformation customerId={customerId} goToNextStep={goToNextStep} />
      ),
    },
    {
      component: () => <Summary customerId={customerId} goToNextStep={goToNextStep} />,
    },
    {
      component: () => (
        <ApplicationDocumentModal customerId={customerId} goToSuccessStep={goToSuccessStep} />
      ),
    },
  ];

  const stepsVertical = useMemo(() => {
    const isCreditCard = cardType === CardSelectionType.CREDITCARD;

    const stepDefinitions = [
      {
        text: t('cardEntry_stepper_productStep_title'),
      },
      {
        text: t('cardEntry_stepper_customerStep_title'),
        subSteps: [
          {
            text: t('cardEntry_stepper_customerStepSub1_title'),
          },
          {
            text: t('cardEntry_stepper_customerStepSub2_title'),
          },
          {
            text: t('cardEntry_stepper_customerStepSub3_title'),
          },
        ],
      },
      ...(isCreditCard
        ? [
            {
              text: t('cardEntry_stepper_scoringStep_title'),
            },
          ]
        : []),
      {
        text: t('cardEntry_stepper_customerDetailStep_title'),
        subSteps: [
          {
            text: t('cardEntry_stepper_customerDetailStepSub1_title'),
          },
          {
            text: t('cardEntry_stepper_customerDetailStepSub2_title'),
          },
        ],
      },
      {
        text: t('cardEntry_stepper_3rdPartyStep_title'),
      },
      {
        text: t('cardEntry_stepper_contactStep_title'),
        subSteps: [
          {
            text: t('cardEntry_stepper_contactStepSub1_title'),
          },
          {
            text: t('cardEntry_stepper_contactStepSub2_title'),
          },
        ],
      },
      {
        text: t('cardEntry_stepper_cardStep_title'),
        subSteps: [
          ...(isProductOffer
            ? [
                {
                  text: t('cardEntry_stepper_productOffersStep_title'),
                },
              ]
            : []),
          {
            text: t('cardEntry_stepper_cardStepSub1_title'),
          },
          {
            text: t('cardEntry_stepper_cardStepSub2_title'),
          },
        ],
      },
      {
        text: t('cardEntry_stepper_summaryStep_title'),
      },
      {
        text: t('cardEntry_stepper_documentUploadStep_title'),
      },
    ];

    return stepDefinitions.map((item, index) => ({
      text: item.text,
      index: index + 1,
      ...(item?.subSteps
        ? {
            subSteps: item.subSteps?.map((subItem, subIndex) => ({
              text: subItem.text,
              index: subIndex + 1,
            })),
          }
        : {}),
    }));
  }, [cardType, isProductOffer, t]);

  return <Application stepperRef={stepperRef} steps={steps} stepsVertical={stepsVertical} />;
};

export default (): ReactElement => (
  <BankruptProvider>
    <CardEntryProvider>
      <CardEntry />
    </CardEntryProvider>
  </BankruptProvider>
);
